import React, { useCallback } from "react";

import { Run, VcsProvider } from "types/generated";
import { Commit } from "components/icons/generated";
import Button from "ds/components/Button";
import Counter from "ds/components/Counter";
import MetaInfoListItem from "components/MetaInfoList/Item";
import useTypedContext from "hooks/useTypedContext";
import { StackContext } from "views/Stack/Context";
import Box from "ds/components/Box";
import { useToggle } from "hooks/useToggle";
import { getCommitLabel } from "utils/commit";
import Typography from "ds/components/Typography";
import TimestampMetaInfo from "components/Timestamp/MetaInfo";
import CardTitle from "ds/components/Card/components/Title";
import CardCollapsible from "ds/components/Card/Collapsible";
import { providerHasVcsHost } from "utils/vcs";
import { withTestId } from "utils/withTestId";

import CommitAuthorLink from "./CommitAuthorLink";
import DriftDetectedLabel from "../../components/DriftDetectedLabel";
import { showStackRunsPushCommitsDrawer } from "../PushCommitsDrawer";
import styles from "./styles.module.css";

type RunsGroupProps = {
  run: Run;
  provider: VcsProvider;
  count: number;
  driftDetectedCount?: number;
  isOpen?: boolean;
  children?: React.ReactNode;
  pushCommits?: string[];
  someRunsShouldBeApproved: boolean;
};

const RunsGroup = (props: RunsGroupProps) => {
  const {
    run,
    provider,
    children,
    count,
    driftDetectedCount = 0,
    isOpen = false,
    pushCommits = [],
    someRunsShouldBeApproved,
  } = props;
  const [isCollapsed, toggle] = useToggle(!isOpen);

  const { stack } = useTypedContext(StackContext);

  const handleToggle = useCallback(() => {
    toggle();
  }, [toggle]);

  const hasVcsHost = providerHasVcsHost(provider);
  const commitTooltip = stack.trackedCommit?.hash === run.commit.hash ? "Tracked commit" : null;
  const hasMultiplePushCommits = pushCommits.length > 1;
  const shortCommitLabel = getCommitLabel(provider, run.commit.hash);
  const commitLabel = hasMultiplePushCommits ? `${shortCommitLabel} (HEAD)` : shortCommitLabel;

  const handleShowPushCommits = useCallback(() => {
    showStackRunsPushCommitsDrawer({
      commits: pushCommits,
      headCommit: run.commit,
      provider,
    });
  }, [pushCommits, run.commit, provider]);

  return (
    <CardCollapsible
      headerAlign="start"
      ariaLevel={3}
      ariaLabel={run.title}
      isCollapsed={isCollapsed}
      onToggle={handleToggle}
      fullbleed
      title={
        <Box direction="column" gap="medium" fullWidth>
          <Box justify="between">
            <Box align="center">
              <CardTitle tag="span">{run.title}</CardTitle>
              <Box padding="0 medium">
                <Counter count={count} />
              </Box>
            </Box>
            {(someRunsShouldBeApproved || hasMultiplePushCommits) && (
              <Box align="center" gap="large">
                {someRunsShouldBeApproved && (
                  <Typography tag="span" variant="p-body2" color="warning">
                    Needs approval
                  </Typography>
                )}
                {hasMultiplePushCommits && (
                  <Button
                    variant="secondary"
                    size="small"
                    onPress={handleShowPushCommits}
                    {...withTestId("view-all-push-commits-button")}
                  >
                    {`View all ${pushCommits.length} commits`}
                  </Button>
                )}
              </Box>
            )}
          </Box>

          <Box gap="large" className={styles.meta}>
            <TimestampMetaInfo
              prefix="Committed on"
              timestamp={run.commit.timestamp}
              postfix={
                <>
                  by&nbsp;
                  <CommitAuthorLink
                    login={run.commit.authorLogin || undefined}
                    name={run.commit.authorName}
                    provider={provider}
                  />
                </>
              }
            />
            {!hasVcsHost && (
              <MetaInfoListItem icon={Commit} tooltip={commitTooltip}>
                {commitLabel}
              </MetaInfoListItem>
            )}
            {hasVcsHost && (
              <MetaInfoListItem
                href={run.commit.url}
                linkText={commitLabel}
                icon={Commit}
                target="_blank"
                tooltip={commitTooltip}
              />
            )}
            <DriftDetectedLabel count={driftDetectedCount} />
          </Box>
        </Box>
      }
    >
      <div className={styles.wrapper}>{children}</div>
    </CardCollapsible>
  );
};

export default RunsGroup;
